import { Asset } from '@/api/generatedApi';
import { useCompareButton } from '@/hooks/useCompareButton';
import { AssetPageObjectModel } from '@/poms/assetPageObjectModel';
import { getAssetName } from '@/utils/assets';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import CompareIcon from '@mui/icons-material/Compare';
import { Box, Button, IconButton, Menu, MenuItem, Tooltip, styled } from '@mui/material';
import { Thumbnail } from '../Thumbnail';

const IconOverlayWrapper = styled('span')({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  verticalAlign: 'middle',
});

const CornerCheck = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: -4,
  width: 14,
  height: 14,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  color: theme.palette.common.white,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
}));

const MenuItemIcon = styled('span')(({ theme }) => ({
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  color: theme.palette.common.white,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
}));

const MenuItemContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ThumbnailWrapper = styled('span')({
  display: 'inline-flex',
  flexShrink: 0,
});

export const AddToCompareButton = ({
  asset,
  variant = 'button',
  size = 'small',
  quickCompare,
  className,
}: {
  asset: Asset;
  variant?: 'button' | 'icon';
  size?: 'small' | 'medium' | 'large';
  quickCompare?: string;
  className?: string;
}) => {
  const {
    anchorElement,
    disabled,
    handleClick,
    handleClose,
    handleQuickCompare,
    handleCompareToSelected,
    handleSelectForCompare,
    hasQuickCompare,
    hasSelectedAsset,
    hidden,
    isSelected,
    open,
    quickCompareAsset,
    selectedAsset,
    shouldShowPopover,
    tooltip,
  } = useCompareButton({ asset, quickCompare });

  if (hidden) {
    return;
  }

  return (
    <>
      <Tooltip title={tooltip}>
        {variant === 'icon' ? (
          <span>
            <IconButton
              size={size}
              onClick={handleClick}
              disabled={disabled}
              className={className}
              color={'default'}
              data-testid={AssetPageObjectModel.AddToCompareIcon}
            >
              <IconOverlayWrapper>
                <CompareIcon color='inherit' />
                {isSelected && (
                  <CornerCheck>
                    <CheckIcon fontSize='inherit' />
                  </CornerCheck>
                )}
              </IconOverlayWrapper>
            </IconButton>
          </span>
        ) : (
          <span>
            <Button
              size={size}
              variant='text'
              color='primary'
              startIcon={
                <IconOverlayWrapper>
                  <CompareIcon fontSize='small' color='inherit' />
                  {isSelected && (
                    <CornerCheck>
                      <CheckIcon fontSize='inherit' />
                    </CornerCheck>
                  )}
                </IconOverlayWrapper>
              }
              endIcon={shouldShowPopover ? <ArrowDropDownIcon /> : undefined}
              onClick={handleClick}
              disabled={disabled}
              className={className}
              data-testid={AssetPageObjectModel.AddToCompareButton}
            >
              Compare
            </Button>
          </span>
        )}
      </Tooltip>
      {shouldShowPopover && (
        <Menu
          anchorEl={anchorElement}
          open={open}
          onClose={handleClose}
          data-testid={AssetPageObjectModel.AddToCompareMenu}
        >
          {hasQuickCompare && quickCompareAsset && (
            <MenuItem onClick={handleQuickCompare}>
              <MenuItemContent>
                <span>Compare</span>
                <Tooltip title={getAssetName(quickCompareAsset)}>
                  <ThumbnailWrapper>
                    <Thumbnail asset={quickCompareAsset} size={32} noLink />
                  </ThumbnailWrapper>
                </Tooltip>
                <span>to</span>
                <Tooltip title={getAssetName(asset)}>
                  <ThumbnailWrapper>
                    <Thumbnail asset={asset} size={32} noLink />
                  </ThumbnailWrapper>
                </Tooltip>
              </MenuItemContent>
            </MenuItem>
          )}
          {hasSelectedAsset && selectedAsset && (
            <MenuItem onClick={handleCompareToSelected}>
              <MenuItemContent>
                <span>Compare</span>
                <Tooltip title={getAssetName(selectedAsset)}>
                  <ThumbnailWrapper>
                    <Thumbnail asset={selectedAsset} size={32} noLink />
                  </ThumbnailWrapper>
                </Tooltip>
                <span>to</span>
                <Tooltip title={getAssetName(asset)}>
                  <ThumbnailWrapper>
                    <Thumbnail asset={asset} size={32} noLink />
                  </ThumbnailWrapper>
                </Tooltip>
              </MenuItemContent>
            </MenuItem>
          )}
          <MenuItem onClick={handleSelectForCompare}>
            <MenuItemContent>
              <span>Select for compare</span>
              <MenuItemIcon>
                <CheckIcon fontSize='inherit' />
              </MenuItemIcon>
            </MenuItemContent>
          </MenuItem>
        </Menu>
      )}
    </>
  );
};
